import { t } from '@bia-auction/shared-i18n';
import type { MyBidsPage as MyBidsPageData, MyStats } from '@bia-auction/shared-schema';
import { myBidsPageSchema, myStatsSchema } from '@bia-auction/shared-schema';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  SkeletonRows,
} from '@bia-auction/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ExternalLink, Gavel } from 'lucide-react';
import { ErrorMsg } from '../components/Feedback';
import { PageHero } from '../components/PageHero';
import { api } from '../lib/api';
import { formatCount, formatDateTime, formatKurus } from '../lib/format';
import { PHOTOS } from '../lib/photos';

/**
 * The bidder's own panel (`/me`) — "kendine ait bir alan" (Bia feedback, 11.08.2026). Before
 * this, a signed-in bidder landed on the auction list and had nothing of their own: Özcan
 * asked that they "see their own history — how many they entered, how many they struck,
 * whether they got one or not", and Fatih wanted it to feel like a panel rather than a list.
 *
 * Two limits carried over from the admin overview (`web-admin/src/routes/DashboardPage.tsx`):
 * - **Counters, not analytics.** Spend over time, win rate and the like are the P2 analytics
 *   dashboard (`docs/01-prd.md` non-goals).
 * - **The server returns raw per-state counts**; the buckets below are derived here, so a lot
 *   lifecycle change doesn't need an API change.
 *
 * The recent list reuses the existing `GET /me/bids` — this page adds one endpoint, not two.
 */

/** How many recent bids the panel previews before handing off to the full list. */
const RECENT_LIMIT = 5;

/**
 * How often the counters re-poll while the tab is focused. A minute is far slower than the
 * live console (that is what the socket is for) but fast enough that "önde olduğum" is not
 * telling a bidder something that stopped being true half an hour ago.
 */
const STATS_REFRESH_MS = 60_000;

/**
 * Same rule as `MyBidsPage`: link back to the car only while there is still something to do
 * there — a running auction, or one not yet started (where an early bid is exactly this row).
 */
function isActionable(state: MyBidsPageData['items'][number]['saleState']): boolean {
  return state === 'LIVE' || state === 'PAUSED' || state === 'PUBLISHED';
}

/** docs/15 §1: a visible focus state is an accessibility requirement, not a nicety. */
const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const LINK = `inline-flex items-center gap-1 rounded-sm text-tx-brand hover:underline ${FOCUS_RING}`;

export function DashboardPage() {
  const stats = useQuery({
    queryKey: ['me', 'stats'],
    queryFn: () => api<MyStats>('/me/stats', {}, myStatsSchema),
    // "Currently leading" is the one tile that goes wrong by standing still: a bidder outbid
    // while this page is open would keep reading that they hold the top bid. The app disables
    // refetch-on-focus globally (`main.tsx`), which is right for the static pages and wrong
    // here, so this query opts back in and adds a slow poll. `refetchIntervalInBackground`
    // defaults to false, so the timer only runs while the tab is actually focused — this
    // costs nothing for the dashboards left open in a background tab.
    refetchInterval: STATS_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
  const recent = useQuery({
    queryKey: ['me', 'bids', RECENT_LIMIT, 0, 'dashboard'],
    queryFn: () =>
      api<MyBidsPageData>(
        `/me/bids?limit=${RECENT_LIMIT}&offset=0`,
        {},
        myBidsPageSchema,
      ),
  });

  // A bidder who has never bid gets the invitation, not a wall of zeros.
  const noHistory = stats.data?.bids === 0;

  return (
    <div className="space-y-6">
      {/* The hero sits outside every branch, as on the showcase: the title band is the one
          thing this route can render before its query resolves, and a page that shows only a
          skeleton has nothing to say it is the right page. The subline waits for real data. */}
      <PageHero
        photo={PHOTOS.yardWide}
        title={t('bidder.dashboard.title')}
        subline={
          stats.data && !noHistory
            ? // Two counts, so two messages: English pluralises each independently and a single
              // message can only agree with one of them. The separator is punctuation shared by
              // both locales, so it stays here rather than becoming a translatable string.
              [
                t('bidder.dashboard.sublineSales', { n: stats.data.sales }),
                t('bidder.dashboard.sublineBids', { n: stats.data.bids }),
              ].join(' · ')
            : undefined
        }
      />

      {stats.isLoading ? (
        <SkeletonRows rows={4} />
      ) : stats.isError ? (
        <ErrorMsg error={stats.error} />
      ) : noHistory ? (
        <EmptyState
          icon={Gavel}
          title={t('bidder.dashboard.empty')}
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/">{t('bidder.nav.auctions')}</Link>
            </Button>
          }
        />
      ) : (
        <>
          {stats.data && <StatsSections stats={stats.data} />}

          <section className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-head text-h3 font-semibold">{t('bidder.dashboard.recent')}</h2>
              <Link
                to="/me/bids"
                className={`rounded-sm text-sm text-tx-brand hover:underline ${FOCUS_RING}`}
              >
                {t('bidder.dashboard.viewAll')}
              </Link>
            </div>
            {recent.isLoading ? (
              <SkeletonRows rows={3} />
            ) : recent.isError ? (
              <ErrorMsg error={recent.error} />
            ) : (
              <RecentBids items={recent.data?.items ?? []} />
            )}
          </section>
        </>
      )}
    </div>
  );
}

function StatsSections({ stats }: { stats: MyStats }) {
  const lot = (k: keyof MyStats['lots']): number => stats.lots[k] ?? 0;

  // Every lot the caller has bid on, whatever became of it — derived from the buckets the
  // response actually carries rather than enumerated here, so a state added to `lotStateSchema`
  // can't silently drop out of the total (the same trap the server-side seeding had). The
  // groupings below stay explicit: those are semantic choices, not a sum of everything.
  const lotsBidOn = Object.values(stats.lots).reduce((n, v) => n + (v ?? 0), 0);
  // Decided = SOLD or UNSOLD. `won` is a subset of SOLD, so the remainder is what was lost.
  // REMOVED is in neither bucket: a withdrawn lot was never a contest to win or lose.
  const lost = Math.max(0, lot('SOLD') + lot('UNSOLD') - stats.won);
  const pending = lot('OPEN') + lot('SCHEDULED');

  return (
    <>
      <section className="space-y-2">
        <h2 className="font-head text-h3 font-semibold">
          {t('bidder.dashboard.section.participation')}
        </h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label={t('bidder.dashboard.sales')} value={stats.sales} />
          <Stat label={t('bidder.dashboard.lots')} value={lotsBidOn} />
          <Stat label={t('bidder.dashboard.bids')} value={stats.bids} />
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="font-head text-h3 font-semibold">
          {t('bidder.dashboard.section.results')}
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label={t('bidder.dashboard.won')} value={stats.won} />
          <Stat label={t('bidder.dashboard.lost')} value={lost} />
          <Stat
            label={t('bidder.dashboard.pending')}
            hint={t('bidder.dashboard.pendingHint')}
            value={pending}
          />
          <Stat
            label={t('bidder.dashboard.leading')}
            hint={t('bidder.dashboard.leadingHint')}
            value={stats.leading}
          />
        </div>
      </section>
    </>
  );
}

/** One counter tile — headline figure with an optional line of context beneath. */
function Stat({ label, hint, value }: { label: string; hint?: string; value: number }) {
  return (
    <Card>
      <CardContent className="space-y-1 p-5">
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="font-head text-display font-semibold tabular-nums">
          {formatCount(value)}
        </div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

/** The last few bids as cards — the full, paged, sortable view stays on `/me/bids`. */
function RecentBids({ items }: { items: MyBidsPageData['items'] }) {
  if (items.length === 0) return <EmptyState icon={Gavel} title={t('bidder.myBids.empty')} />;
  return (
    <div className="space-y-3">
      {items.map((b) => (
        <Card key={b.id}>
          <CardContent className="space-y-1 p-4 text-sm">
            <div className="flex items-start justify-between gap-2">
              <span className="font-medium">{b.saleTitle ?? b.saleCode}</span>
              <Badge variant="secondary">{t(`bidKind.${b.kind}`)}</Badge>
            </div>
            <div className="text-muted-foreground tabular-nums">
              {isActionable(b.saleState) ? (
                <Link
                  to="/auctions/$auctionId/lots/$lotId"
                  params={{ auctionId: b.saleId, lotId: b.lotId }}
                  className={LINK}
                >
                  {b.plateSnapshot ?? t('bidder.myBids.viewVehicle')}
                  <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                </Link>
              ) : (
                (b.plateSnapshot ?? t('common.none'))
              )}
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="font-head text-h3 font-semibold tabular-nums">
                {formatKurus(b.amount, b.currency)}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {formatDateTime(b.placedAt)}
              </span>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
